import { Box, Button, Stack, Typography } from "@mui/material";
import zoomSdk, { Apis, GetMeetingContextResponse } from "@zoom/appssdk";
import { useEffect, useRef, useState } from "react";
import "./App.css";
import ErrorModal from "./components/ErrorModal";
import LoadingScreen from "./components/LoadingScreen";
import MainClient from "./components/MainClient";
import Meeting from "./components/Meeting";
import Navbar from "./components/navbar";
import { apis } from "./zoomSdkConfig";

const getInitialEnvironment = (): string | null => {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get("environment");
    if (fromUrl) {
      sessionStorage.setItem("environment", fromUrl);
      return fromUrl;
    }
    return sessionStorage.getItem("environment");
  } catch (e) {
    console.error(e);
    return null;
  }
};

const Home = () => {
  const [setupDone, setSetupDone] = useState(false);
  const [error, setError] = useState<string>("");
  const [runningContext, setRunningContext] = useState("");
  const [meetingContext, setMeetingContext] = useState<GetMeetingContextResponse>();
  const [user, setUser] = useState<any>(null);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [token, setToken] = useState<string | null>(null);
  const [codeChallenge, setCodeChallenge] = useState<string>("");
  const [environment] = useState<string | null>(getInitialEnvironment);

  const [authFailed, setAuthFailed] = useState(false);
  const [authorizing, setAuthorizing] = useState(false);

  // The onAuthorized handler is registered once (below) but authorize()
  // can be called many times across retries, so codeChallenge needs to be
  // read from a ref inside the handler rather than captured in a closure.
  const codeChallengeRef = useRef<string>("");
  const authCodeInFlightRef = useRef<string | null>(null);
  const handledAuthCodesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    codeChallengeRef.current = codeChallenge;
  }, [codeChallenge]);

  // Register the onAuthorized listener exactly once, regardless of how
  // many times authorize() is retried. Avoids relying on whether the SDK
  // dedupes repeated onAuthorized registrations.
  useEffect(() => {
    zoomSdk.onAuthorized(async (e) => {
      const code = e.code;

      // Already processed this exact code, or a request is already in flight —
      // ignore the duplicate call instead of firing a second token request.
      if (handledAuthCodesRef.current.has(code) || authCodeInFlightRef.current) {
        return;
      }

      authCodeInFlightRef.current = code;

      try {
        const tokenResponse = await (
          await fetch(
            `/api/zoomapp/token/${environment}?code=${code}&href=${e.redirectUri}&codeChallenge=${codeChallengeRef.current}`
          )
        )
          .json()
          .catch((err) => {
            console.error(err);
            return null;
          });

        if (tokenResponse?.accessToken) {
          handledAuthCodesRef.current.add(code);
          setToken(tokenResponse.accessToken);
          localStorage.setItem("token", tokenResponse.accessToken);
          setAuthFailed(false);
        } else {
          throw new Error("No access token in response");
        }
      } catch (err) {
        console.error(err);
        setAuthFailed(true);
      } finally {
        authCodeInFlightRef.current = null;
        setAuthorizing(false);
        setLoadingMessage("");
      }
    });
  }, []);

  useEffect(() => {
    const configureSdk = async () => {
      try {
        setLoadingMessage("Configuring Zoom App SDK...");
        const configResponse = await zoomSdk.config({
          version: "0.16.0",
          capabilities: [...apis.map((item) => item as Apis)],
        });
        setRunningContext(configResponse.runningContext);
        setSetupDone(true);
      } catch (error) {
        setError(
          "There was an error configuring the Zoom SDK, please contact support"
        );
      }
    };
    configureSdk();
  }, []);

  useEffect(() => {
    if (setupDone) {
      const token = localStorage.getItem("token");
      if (token) {
        setToken(token);
      } else {
        authorize();
      }
    }
  }, [setupDone]);

  useEffect(() => {
    if (token) {
      fetchUser();
    }
  }, [token]);

  const getMeetingContext = async () => {
    setLoadingMessage("Fetching meeting details...");
    zoomSdk
      .getMeetingContext()
      .then((e) => {
        setMeetingContext(e);
      })
      .catch((e) => { })
      .finally(() => {
        setLoadingMessage("");
      });
  };

  const authorize = async () => {
    setAuthFailed(false);
    setAuthorizing(true);
    setLoadingMessage("Authorizing...");

    localStorage.removeItem("token");

    let authorizeResponse;
    try {
      authorizeResponse = await (await fetch(`/api/zoomapp/code/${environment}`))
        .json()
        .catch((e) => {
          console.error(e);
          return null;
        });

      if (!authorizeResponse?.codeChallenge) {
        setAuthFailed(true);
        setAuthorizing(false);
        setLoadingMessage("");
        return;
      }
    } catch (error) {
      console.error(error);
      setAuthFailed(true);
      setAuthorizing(false);
      setLoadingMessage("");
      return;
    }

    const { codeChallenge } = authorizeResponse;
    setCodeChallenge(codeChallenge);
    codeChallengeRef.current = codeChallenge; // set synchronously too, don't wait on the effect
    const authorizeOptions = {
      codeChallenge: codeChallenge,
    };
    try {
      await zoomSdk.authorize(authorizeOptions);
      // Success here just means the consent flow kicked off — the actual
      // result lands in the onAuthorized handler registered above.
    } catch (e) {
      console.error(e);
      setAuthFailed(true);
      setAuthorizing(false);
      setLoadingMessage("");
    }
  };

  const fetchUser = async () => {
    try {
      setLoadingMessage("Fetching user details...");
      const response = await fetch("/api/zoomapp/me", {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });
      if (response.status !== 200) throw new Error();
      const user = await response.json();
      setUser(user);
      setLoadingMessage("");
      if (runningContext === "inMeeting") {
        getMeetingContext();
      }
    } catch (error) {
      console.error(error);
      localStorage.removeItem("token");
      setToken(null);
      authorize();
    }
  };

  const downloadFile = async (id: string) => {
    try {
      const encodedToken = encodeURIComponent(token ?? "");
      const downloadUrl = `${window.location.origin}/api/zoomapp/recordings/download/phone?meetingId=${id}&token=${encodedToken}`;
      zoomSdk.openUrl({ url: downloadUrl }).catch((e) => {
        console.error(e);
        setError("Failed to download, please refresh app");
      });
    } catch (error) {
      console.error(error);
    }
  };

  const reUploadPhoneMedia = async (id: string) => {
    try {
      const response = await fetch(`api/zoomapp/recordings/upload/phone?callId=${id}`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });
      if (response.status !== 200) throw new Error();
    } catch (error) {
      console.error(error);
    }
  };

  if (authFailed) {
    return (
      <Stack
        sx={{
          height: "100vh",
          width: "100vw",
          backgroundColor: "rgb(31, 29, 43)",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          px: 3,
          textAlign: "center",
        }}
      >
        <Typography sx={{ color: "white" }}>
          We couldn't sign you in. Please try authorizing again.
        </Typography>
        <Button
          variant="contained"
          disabled={authorizing}
          onClick={() => authorize()}
        >
          {authorizing ? "Retrying..." : "Try again"}
        </Button>
      </Stack>
    );
  }

  return loadingMessage ? (
    <LoadingScreen message={loadingMessage} />
  ) : (
    <Stack
      sx={{
        height: "100vh",
        width: "100vw",
        backgroundColor: "rgb(31, 29, 43)",
        overflow: "hidden",
      }}
      direction="column"
    >
      <Navbar />
      <Box sx={{ flexGrow: 1, minHeight: 0, display: "flex", flexDirection: "column", p: 2, overflowY: "auto" }}>
        {user && meetingContext ? (
          <Meeting
            user={user}
            meetingContext={meetingContext}
            accessToken={token ?? ""}
            environment={environment ?? ""}
          />
        ) : (
          <MainClient
            user={user}
            accessToken={token ?? ""}
            downloadFile={downloadFile}
            reUploadMedia={reUploadPhoneMedia}
            startMeeting={() => zoomSdk.launchAppInMeeting()}
            authorize={authorize}
            environment={environment ?? ""}
          />
        )}
      </Box>
      <ErrorModal
        open={error !== ""}
        onClose={() => {
          setError("");
        }}
        title=""
        message={error ?? ""}
      />
    </Stack>
  );
};

export default Home;